import { validateEnumTranslations } from '../../safety_checks';
import { PROJECT_CONFIG } from '../project_branding/config';
import { PLANS } from '../subscription';

/**
 * Source de vérité des libellés / descriptions d'addons (subscription v2).
 *
 * Le `AddonCode` interne (`subscription.ts`) est en lowercase (`extra_user`,
 * `white_label`, ...) — la convention historique de `fr-FR.ts` utilise un
 * suffixe UPPERCASE. On expose donc :
 *  - un mapping lowercase → uppercase (`ADDON_KEY_BY_CODE`) pour que le
 *    consommateur (front/back) construise dynamiquement la clé de traduction
 *    à partir de l'`AddonCode`.
 *  - l'objet de traductions spread dans `fr-FR.ts`.
 *  - `validateEnumTranslations()` garantit qu'un addon ajouté à `ADDONS`
 *    sans entrée de traduction casse la build.
 */
/** Namespace de `fr-FR.ts` qui reçoit les libellés/descriptions d'addons (spread ci-dessous). */
export const ADDON_TRANSLATION_NAMESPACE = 'PROFESSIONAL_APP_SUBSCRIPTION_PAGE';
export const TRANSLATE_KEY_ADDON_LABEL = 'ADDON_LABEL_';
export const TRANSLATE_KEY_ADDON_DESCRIPTION = 'ADDON_DESCRIPTION_';

export const AddonKey = {
  EXTRA_USER: 'EXTRA_USER',
  EXTRA_GMB: 'EXTRA_GMB',
  EXTRA_DOMAIN: 'EXTRA_DOMAIN',
  WHITE_LABEL: 'WHITE_LABEL',
  SMART_RIDE: 'SMART_RIDE',
  MOBILE_APP_WHITELABEL: 'MOBILE_APP_WHITELABEL',
  SEO_EXTRA_BACKLINK: 'SEO_EXTRA_BACKLINK',
  SEO_ARTICLES_DOUBLE: 'SEO_ARTICLES_DOUBLE',
} as const;
export type AddonKey = (typeof AddonKey)[keyof typeof AddonKey];

export const ADDON_KEY_BY_CODE: Record<string, AddonKey> = {
  extra_user: AddonKey.EXTRA_USER,
  extra_gmb: AddonKey.EXTRA_GMB,
  extra_domain: AddonKey.EXTRA_DOMAIN,
  white_label: AddonKey.WHITE_LABEL,
  smart_ride: AddonKey.SMART_RIDE,
  mobile_app_whitelabel: AddonKey.MOBILE_APP_WHITELABEL,
  seo_extra_backlink: AddonKey.SEO_EXTRA_BACKLINK,
  seo_articles_double: AddonKey.SEO_ARTICLES_DOUBLE,
};

export const AddonLabelTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_ADDON_LABEL}${AddonKey.EXTRA_USER}`]: 'Membres d’équipe supplémentaires',
    [`${TRANSLATE_KEY_ADDON_LABEL}${AddonKey.EXTRA_GMB}`]: 'Fiches Google supplémentaires',
    [`${TRANSLATE_KEY_ADDON_LABEL}${AddonKey.EXTRA_DOMAIN}`]: 'Nom de domaine supplémentaire',
    [`${TRANSLATE_KEY_ADDON_LABEL}${AddonKey.WHITE_LABEL}`]: 'Marque blanche',
    [`${TRANSLATE_KEY_ADDON_LABEL}${AddonKey.SMART_RIDE}`]: 'Smart Ride (VTC / Taxi)',
    [`${TRANSLATE_KEY_ADDON_LABEL}${AddonKey.MOBILE_APP_WHITELABEL}`]: 'Application mobile en marque blanche',
    [`${TRANSLATE_KEY_ADDON_LABEL}${AddonKey.SEO_EXTRA_BACKLINK}`]: 'Liens supplémentaires (référencement)',
    [`${TRANSLATE_KEY_ADDON_LABEL}${AddonKey.SEO_ARTICLES_DOUBLE}`]: 'Doubler mes articles (référencement)',
  },
};

export const AddonDescriptionTranslation = {
  'fr-FR': {
    [`${TRANSLATE_KEY_ADDON_DESCRIPTION}${AddonKey.EXTRA_USER}`]: `Réservé à la formule ${PLANS.max.label} Équipe, qui inclut déjà ${PLANS.max.prices.team.included_users} membres : à partir du ${PLANS.max.prices.team.included_users + 1}e, ajoutez des membres avec leur agenda dédié. Pour travailler à plusieurs sur une autre formule, choisissez sa version Équipe (${PLANS.starter.prices.team.included_users} membres inclus en ${PLANS.starter.label}, ${PLANS.pro.prices.team.included_users} en ${PLANS.pro.label}).`,
    [`${TRANSLATE_KEY_ADDON_DESCRIPTION}${AddonKey.EXTRA_GMB}`]:
      'Connectez plusieurs établissements supplémentaires à votre fiche Google Business.',
    [`${TRANSLATE_KEY_ADDON_DESCRIPTION}${AddonKey.EXTRA_DOMAIN}`]:
      'Un nom de domaine .fr/.com en plus, facturé annuellement.',
    [`${TRANSLATE_KEY_ADDON_DESCRIPTION}${AddonKey.WHITE_LABEL}`]: `Retirez le branding ${PROJECT_CONFIG.brand_string} de votre site et de vos emails, et personnalisez le contenu et les délais de vos messages automatiques.`,
    [`${TRANSLATE_KEY_ADDON_DESCRIPTION}${AddonKey.SMART_RIDE}`]:
      'Module dédié aux pros VTC / Taxi : courses, tarification kilométrique, etc.',
    [`${TRANSLATE_KEY_ADDON_DESCRIPTION}${AddonKey.MOBILE_APP_WHITELABEL}`]:
      'Une appli iOS et Android à votre image, distribuée sous votre nom.',
    // Pack référencement (formule Max, pack actif, hors essai). Effet au 1er du mois suivant, sans
    // prorata ni engagement ; jamais le nom du fournisseur de liens.
    [`${TRANSLATE_KEY_ADDON_DESCRIPTION}${AddonKey.SEO_EXTRA_BACKLINK}`]:
      'Chaque mois, un article sur vous publié sur un site de votre secteur, avec un lien vers votre site. De 1 à 3 par mois, à partir du 1er du mois suivant, sans engagement.',
    [`${TRANSLATE_KEY_ADDON_DESCRIPTION}${AddonKey.SEO_ARTICLES_DOUBLE}`]:
      '4 articles par semaine au lieu de 2 sur votre site, à partir du 1er du mois suivant. Vous validez les sujets et les textes comme aujourd’hui. Sans engagement.',
  },
};

//DONT TOUCH below this line
validateEnumTranslations(AddonKey, AddonLabelTranslation, TRANSLATE_KEY_ADDON_LABEL, true);
validateEnumTranslations(AddonKey, AddonDescriptionTranslation, TRANSLATE_KEY_ADDON_DESCRIPTION, true);
